<template>
  <div class="animated fadeIn col-md-12">
    <b-row>
      <b-col cols="12" class="p-0">
        <b-card>
          <div class="text-center" v-show="isLoading">
            <b-spinner variant="primary" label="Text Centered"></b-spinner>
          </div>
          <div slot="header">
            <div class="float-left">
              {{ $lang.edit }} <strong v-text="$lang.template"></strong>
            </div>
            <div class="float-right">
              {{ $lang.searchTemplateFields.id }}&nbsp; :&nbsp; {{ id }}
            </div>
          </div>
          <div v-show="errors.length > 0" class="alert-danger">
            <h4>{{ $lang.error }}</h4>
            <ul v-bind:key="index" v-for="(error, index) in errors">
              <li>{{ error }}</li>
            </ul>
          </div>
          <b-card v-show="!isLoading" no-body="">
            <div slot="header">
              <strong>{{ $lang.base_info }}</strong>
              <div class="float-right">
                <b-button
                  @click="isCollapsed = !isCollapsed"
                  variant="success"
                  class="float-right"
                  size="sm"
                >
                  <mdicon v-if="isCollapsed" name="chevron-up" />
                  <mdicon v-else name="chevron-down" />
                </b-button>
              </div>
            </div>
            <b-collapse v-model="isCollapsed" class="mt-2">
              <b-row class="m-0">
                <b-col cols="12">
                  <Alert
                    v-if="!isEditable"
                    :text="$lang.templateEditableMessage"
                    variant="light-warning"
                  />
                </b-col>
              </b-row>
              <b-form @submit.prevent="save">
                <b-col cols="12">
                  <b-form-group
                    :label="$lang.company"
                    label-for="company_id"
                    :label-cols="this.$config.form.labelCol"
                  >
                    <requiredComponent
                      slot="label"
                      :label="$lang.company"
                      :rules="$v.company_id.$params"
                    />
                    <selectAsync
                      :options="company_options"
                      v-model="$v.company_id.$model"
                      label="text"
                      :asyncFind="getCompanyCombo"
                      :isValid="
                        $v.company_id.$dirty ? !$v.company_id.$error : null
                      "
                      :loading="companyLoading"
                      :disabled="!isEditable"
                    />
                    <b-form-invalid-feedback id="company_id-feedback">
                      {{ $lang.error_required | replace($lang.company) }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group
                    :label="$lang.caption"
                    label-for="caption"
                    :label-cols="12"
                  >
                    <b-form-input
                      id="caption"
                      type="text"
                      :autocomplete="$lang.caption"
                      :state="$v.caption.$dirty ? !$v.caption.$error : null"
                      v-model="$v.caption.$model"
                    >
                    </b-form-input>
                    <b-form-invalid-feedback id="caption-feedback"
                      >عنوان ضروری است</b-form-invalid-feedback
                    >
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group
                    :label="$lang.indicatorCategoryRatioControl"
                    label-for="indicatorCategoryRatioControl"
                    :label-cols="this.$config.form.labelCol"
                  >
                    <requiredComponent
                      slot="label"
                      :label="$lang.indicatorCategoryRatioControl"
                      :rules="$v.indicatorCategoryRatioControl.$params"
                    />
                    <selectAsync
                      :disabled="!isEditable"
                      :options="indicatorCategoryRatioControlOptions"
                      v-model="$v.indicatorCategoryRatioControl.$model"
                      label="caption"
                      :isValid="
                        $v.indicatorCategoryRatioControl.$dirty
                          ? !$v.indicatorCategoryRatioControl.$error
                          : null
                      "
                      :loading="indicatorCategoryRatioControlLoading"
                      :asyncFind="getIndicatorCategoryRatioControlOptions"
                      @input="fillIndicatorCategoryRatioControl"
                    />

                    <b-form-invalid-feedback
                      :style="{
                        display: $v.indicatorCategoryRatioControl.$error
                          ? 'block'
                          : 'none',
                      }"
                    >
                      {{
                        $lang.error_required
                          | replace($lang.indicatorCategoryRatioControl)
                      }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group
                    :label="$lang.indicatorCategoryRatioControlValue"
                    label-for="indicatorCategoryRatioControlValue"
                    :label-cols="$config.form.labelCol"
                  >
                    <requiredComponent
                      slot="label"
                      :label="$lang.indicatorCategoryRatioControlValue"
                      :rules="$v.indicatorCategoryRatioControlValue.$params"
                    />
                    <b-form-input
                      :disabled="!isEditable"
                      id="indicatorCategoryRatioControlValue"
                      type="number"
                      step="0.01"
                      :autocomplete="$lang.indicatorCategoryRatioControlValue"
                      :state="
                        $v.indicatorCategoryRatioControlValue.$dirty
                          ? !$v.indicatorCategoryRatioControlValue.$error
                          : null
                      "
                      v-model="$v.indicatorCategoryRatioControlValue.$model"
                    ></b-form-input>
                    <b-form-invalid-feedback
                      id="indicatorCategoryRatioControlValue-feedback"
                      >{{
                        $lang.error_required
                          | replace($lang.indicatorCategoryRatioControlValue)
                      }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <TagComponent
                    category_name="template_level"
                    :label="$lang.level"
                    :parentPermissions="permission"
                    :parentValue="level_selected"
                    :requiredLabel="levelSelectedRequired"
                    :checkValidation="levelSelectedCheckValidation"
                    @updateCheckValidation="levelSelectedUpdateCheckValidation"
                    @getSelected="getLevelSelected"
                  />
                  <TagComponent
                    category_name="template_category"
                    :label="$lang.category"
                    :parentPermissions="permission"
                    :parentValue="category_selected"
                    :requiredLabel="categorySelectedRequired"
                    :checkValidation="categorySelectedCheckValidation"
                    @updateCheckValidation="
                      categorySelectedUpdateCheckValidation
                    "
                    @getSelected="getCategorySelected"
                  />
                </b-col>
                <b-col cols="12">
                  <b-form-group
                    :label="$lang.type"
                    label-for="is_enable"
                    :label-cols="12"
                  >
                    <requiredComponent
                      slot="label"
                      :label="$lang.status"
                      :rules="$v.is_enable.$params"
                    />
                    <b-form-radio-group
                      id="is_enable"
                      :autocomplete="$lang.status"
                      v-model="$v.is_enable.$model"
                      :state="$v.is_enable.$dirty ? !$v.is_enable.$error : null"
                      :options="is_enable_options"
                      buttons
                      button-variant="outline-primary"
                      size="md"
                      name="radio-btn-outline"
                    ></b-form-radio-group>
                  </b-form-group>
                </b-col>
                <b-col>
                  <div slot="footer" class="form-group form-btn">
                    <b-button
                      class="mr-2"
                      variant="outline-danger"
                      @click="handleBack"
                    >
                      <mdicon name="close" size="20" />
                      {{ $lang.cancelButton }}
                    </b-button>
                    <b-button
                      type="submit"
                      variant="success"
                      v-if="isAdmin(permission)"
                    >
                      <mdicon name="check" size="20" />
                      {{ $lang.submitButton }}
                    </b-button>
                  </div>
                </b-col>
              </b-form>
            </b-collapse>
          </b-card>
          <b-form-group class="m-0" v-show="!isLoading">
            <b-row>
              <b-col>
                <CompetencyExerciseTable
                  :id="id"
                  :template_id="$route.params.id"
                  :permissions="permission"
                  :parentLoading="competencyExerciseLoading"
                  :exercises="generateExercisesData"
                  :competencies="generateCompetenciesData"
                  @addExercise="showExercisesList"
                  @addCompetency="showTemplateCompetencies"
                  @submit="putCompetencyExercises"
                  @fetchData="getAllCompetencyExercise"
                />
              </b-col>
            </b-row>
          </b-form-group>
          <b-modal
            no-close-on-backdrop
            variant="primary"
            header-bg-variant="primary"
            content-class="border-primary"
            v-model="view_list_exercise"
            size="xl"
            hide-header
            ok-only
            @ok="addExercise"
          >
            <SelectSearch
              :title="$lang.listExercise"
              :url="allow_exercise_url"
              :allowFields="$lang.ExerciseColum"
              :allowFilter="true"
              :allowCommunityUnity="true"
              :viewCount="20"
              :acMode="true"
              v-model="allExercises"
              @itemSelected="watchExercises"
            />
            <template v-slot:modal-footer>
              <b-button
                class="ml-2"
                size="md"
                variant="danger"
                @click="view_list_exercise = false"
              >
                <mdicon name="close" size="20" />
                {{ $lang.cancelButton }}
              </b-button>
              <b-button
                variant="success"
                v-if="isAdmin(permission)"
                @click="
                  addExercise();
                  view_list_exercise = false;
                "
              >
                <mdicon name="check" size="20" />
                {{ $lang.submitButton }}
              </b-button>
            </template>
          </b-modal>
          <b-modal
            no-close-on-backdrop
            variant="primary"
            header-bg-variant="primary"
            content-class="border-primary"
            v-model="view_template_competencies"
            size="xl"
            hide-header
            ok-only
            @ok="addCompetencies"
          >
            <SelectSearch
              :title="$lang.competency"
              :url="competency_url"
              :allowCommunityUnity="false"
              :allowFields="$lang.template_competency_selected_fields"
              :allowFilter="true"
              :viewCount="30"
              :acMode="true"
              v-model="competenciesModel"
              @itemSelected="watchCompetencies"
            />
            <template v-slot:modal-footer>
              <b-button
                class="ml-2"
                size="md"
                variant="danger"
                @click="view_template_competencies = false"
              >
                <mdicon name="close" size="20" />
                {{ $lang.cancelButton }}
              </b-button>
              <b-button
                variant="success"
                v-if="isAdmin(permission)"
                @click="
                  addCompetencies();
                  view_template_competencies = false;
                "
              >
                <mdicon name="check" size="20" />
                {{ $lang.submitButton }}
              </b-button>
            </template>
          </b-modal>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import SelectSearch from "@/components/base/selectSearch";
import CompetencyExerciseTable from "./competencyExerciseTable.vue";
import TagComponent from "@/components/tag/tagComponent.vue";
import { required } from "vuelidate/lib/validators";
import { pathOr, includes } from "ramda";
import {
  generateApiUrl,
  renderEumOptions,
  isAdmin,
  setUniqueArrayOfObjects,
  generateError,
} from "@/shared/utils";
import { getIndicatorCategoryCombo } from "@/services";
import { Alert } from "@/components/re-design";

export default {
  name: "edit-template",
  components: {
    SelectSearch,
    CompetencyExerciseTable,
    TagComponent,
    Alert,
  },
  validations: {
    caption: {
      required,
    },
    indicatorCategoryRatioControl: {
      required,
    },
    indicatorCategoryRatioControlValue: {
      required,
    },
    company_id: {},
    is_enable: {},
  },
  data() {
    return {
      isCollapsed: false,
      view_template_competencies: false,
      level_selected: null,
      company_id: null,
      company_options: [],
      levelLoading: false,
      companyLoading: false,
      id: this.$route.params.id,
      caption: "",
      permission: [],
      category_selected: null,
      isLoading: false,
      errors: [],
      exercises: [],
      competencies: [],
      view_list_exercise: false,
      exerciseLoading: false,
      allow_exercise_url:
        this.$config.api_url +
        this.$config.kanoon.template_allow_exercise_list.replace(
          "{0}",
          this.$route.params.id
        ),
      competency_exercise: [],
      temp_exercises: [],
      temp_competencies: [],
      competencyExerciseLoading: false,
      is_enable: null,
      allExercises: [],
      allCompetencies: [],
      competenciesModel: [],
      generateExercisesData: [],
      generateCompetenciesData: [],
      levelSelectedRequired: true,
      levelSelectedCheckValidation: false,
      categorySelectedRequired: true,
      categorySelectedCheckValidation: false,
      indicatorCategoryRatioControl: null,
      indicatorCategoryRatioControlOptions: [],
      indicatorCategoryRatioControlLoading: false,
      indicatorCategoryRatioControlValue: null,
      isEditable: false,
    };
  },
  computed: {
    computedExercises() {
      return this.exercises.map((exercise) => {
        exercise.competencies = pathOr([], ["competencies_object"], exercise);
        exercise.nickname = pathOr(null, ["caption"], exercise);
        return exercise;
      });
    },
    computedCompetencies() {
      return this.competencies.map((competency) => {
        competency.nickname = pathOr(null, ["caption"], competency);
        competency.exercises = pathOr([], ["exercise"], competency);
        return competency;
      });
    },
    competency_url() {
      return generateApiUrl(["kanoon", "competency"], [], {
        has_indicator: true,
      });
    },
    is_enable_options() {
      return [
        {
          value: 1,
          text: "فعال",
        },
        {
          value: 0,
          text: "غیرفعال",
        },
      ];
    },
  },
  watch: {
    allCompetencies(data) {
      this.competenciesModel = data.map((item) => item.competency);
    },
  },
  mounted() {
    this.getCompanyCombo();
    this.fetchData();
  },
  methods: {
    pathOr,
    includes,
    generateApiUrl,
    renderEumOptions,
    isAdmin,
    setUniqueArrayOfObjects,
    async getIndicatorCategoryRatioControlOptions(text = null) {
      try {
        this.indicatorCategoryRatioControlLoading = true;
        const { options } = await getIndicatorCategoryCombo(text);
        this.indicatorCategoryRatioControlOptions = options;
      } catch (error) {
        generateError(error);
      } finally {
        this.indicatorCategoryRatioControlLoading = false;
      }
    },
    fillIndicatorCategoryRatioControl({ value }) {
      this.indicatorCategoryRatioControlValue = value;
    },
    generateExerciseItems(items) {
      const coveragePercentItem = items.find((item) => !item.id);
      const exercises = items.filter((item) => item.id);
      const newExercises = exercises.map((item) =>
        this.generateExerciseItem(item)
      );
      if (coveragePercentItem) {
        newExercises.push(this.generateExerciseItem(coveragePercentItem));
      }
      return newExercises;
    },
    generateExerciseItem(item) {
      const caption = pathOr(false, ["caption"], item);
      return {
        assigned: pathOr(false, ["assigned"], item),
        caption,
        competency_indicator_count: pathOr(
          0,
          ["competency_indicator_count"],
          item
        ),
        id: pathOr(0, ["id"], item),
        nickname: pathOr(caption, ["nickname"], item),
        selected_indicator_count: pathOr(0, ["selected_indicator_count"], item),
        status: pathOr(true, ["status"], item),
        coverage_percent: pathOr(0, ["coverage_percent"], item),
      };
    },
    generateNewExerciseItem(item) {
      return {
        id: pathOr(null, ["id"], item),
        caption: pathOr(null, ["caption"], item),
        nickname: pathOr(null, ["caption"], item),
        competency_indicator_count: 0,
        selected_indicator_count: 0,
        status: true,
        assigned: true,
      };
    },
    generateNewCompetencyItem(item) {
      const competency = {
        id: pathOr(null, ["id"], item),
        caption: pathOr(null, ["name"], item),
        nickname: pathOr(null, ["name"], item),
        reference: pathOr(null, ["reference"], item),
      };
      let exercise = this.allExercises.map((exerciseItem) => {
        return {
          ...exerciseItem,
          competency_indicator_count: 0,
          selected_indicator_count: 0,
          coverage_percent: 0,
          status: true,
          assigned: true,
        };
      });
      exercise = setUniqueArrayOfObjects(exercise, "id");
      return {
        competency,
        exercise,
      };
    },
    watchExercises(item, checked) {
      if (checked) {
        const newExerciseItem = this.generateNewExerciseItem(item);
        // add exercise item to exercises
        this.allExercises.push(newExerciseItem);

        // add exercise item to competencies
        this.allCompetencies = this.allCompetencies.map((competency) => {
          const exercises = pathOr([], ["exercise"], competency);
          const competencyItem = pathOr(null, ["competency"], competency);
          return {
            competency: { ...competencyItem },
            exercise: [...exercises, newExerciseItem],
          };
        });
      } else {
        // remove exercise item of exercises
        this.allExercises = this.allExercises.filter(
          (exercise) => exercise.id !== item.id
        );

        // remove exercise item of competencies
        this.allCompetencies = this.allCompetencies.map((competency) => {
          let exercises = pathOr([], ["exercise"], competency);
          exercises = exercises.filter((exercise) => exercise.id !== item.id);
          const competencyItem = pathOr(null, ["competency"], competency);
          return {
            competency: { ...competencyItem },
            exercise: [...exercises],
          };
        });
      }

      this.renderExercisesAndCompetencies();
    },
    watchCompetencies(item, checked) {
      if (checked) {
        // add competency item to competencies
        const newCompetency = this.generateNewCompetencyItem(item);
        this.allCompetencies.push(newCompetency);
      } else {
        // remove competency item of competencies
        this.allCompetencies = this.allCompetencies.filter(
          (competency) =>
            pathOr(null, ["competency", "id"], competency) !== item.id
        );
      }

      this.renderExercisesAndCompetencies();
    },
    renderExercisesAndCompetencies() {
      this.allExercises.push({
        id: 0,
        caption: "درصد پوشش شایستگی",
        nickname: "درصد پوشش شایستگی",
        competency_indicator_count: 0,
        selected_indicator_count: 0,
        coverage_percent: 0,
        status: true,
        assigned: true,
      });
      this.allExercises = setUniqueArrayOfObjects(this.allExercises, "id");
      this.generateExercisesData = this.generateExerciseItems(
        this.allExercises
      );
      this.generateCompetenciesData = this.allCompetencies.map((competency) => {
        const exercises = pathOr([], ["exercise"], competency);
        return {
          id: pathOr(null, ["competency", "id"], competency),
          caption: pathOr(null, ["competency", "caption"], competency),
          nickname: pathOr(null, ["competency", "nickname"], competency),
          competency_weight: pathOr(
            null,
            ["competency", "competency_weight"],
            competency
          ),
          reference: pathOr(null, ["competency", "reference"], competency),
          exercises: this.generateExerciseItems(exercises),
        };
      });
    },
    refreshLevelReference() {},
    refreshReference() {},
    showTemplateCompetencies() {
      this.temp_competencies = this.competencies.map((x) => x);
      this.view_template_competencies = true;
    },
    showExercisesList(competencies) {
      const competenciesId = competencies
        .map((x) => `competency_id[]=${x.id}`)
        .join("&");
      const url = generateApiUrl(
        ["kanoon", "template_allow_exercise_list"],
        [this.id]
      );
      this.allow_exercise_url = `${url}?${competenciesId}`;
      this.view_list_exercise = true;
    },
    handleBack() {
      window.history.length > 1 ? this.$router.go(-1) : this.$router.push("/");
    },
    clear() {
      this.caption = "";
      this.category_selected = null;
    },
    async save() {
      this.levelSelectedCheckValidation = true;
      this.categorySelectedCheckValidation = true;
      this.$v.$touch();
      if (this.$v.$anyError) return;
      if (this.levelSelectedRequired && !this.level_selected) return;
      if (this.categorySelectedRequired && !this.category_selected) return;
      try {
        this.isLoading = true;
        const url = generateApiUrl(["kanoon", "template_id"], [this.id]);
        const payload = {
          company_code: pathOr(null, ["value"], this.company_id),
          caption: this.caption,
          indicator_category_ratio_control_id: pathOr(
            null,
            ["id"],
            this.indicatorCategoryRatioControl
          ),
          indicator_category_ratio_control_value:
            this.indicatorCategoryRatioControlValue,
          category_id: pathOr(null, ["value"], this.category_selected),
          level_id: pathOr(null, ["value"], this.level_selected),
          is_enable: this.is_enable,
        };
        const response = await this.$httpService.put(url, payload);
        this.$notify.success(response.data.message);
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.isLoading = false;
      }
    },
    async getCompanyCombo(text = null) {
      if (text && text.length > 2) {
        this.company_options = [];
      }

      try {
        this.companyLoading = true;
        const url = generateApiUrl(["kanoon", "company_combo"], [], {
          search_txt: text,
        });
        const response = await this.$httpService.get(url);
        const data = pathOr([], ["data", "data"], response);
        this.company_options = data.map((x) => {
          return {
            value: x.code,
            text: x.name,
          };
        });
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.companyLoading = false;
      }
    },
    async putCompetencyExercises(data) {
      try {
        this.competencyExerciseLoading = true;
        const url = generateApiUrl(
          ["kanoon", "template_competency_exercise"],
          [this.id]
        );
        const payload = { data };
        const response = await this.$httpService.put(url, payload);
        this.$notify.success(response.data.message);
        this.fetchData();
        this.getAllCompetencyExercise();
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.competencyExerciseLoading = false;
      }
    },
    rebuildCompetencyExercise(exercises) {
      // اگر شایستگی وجود ندارد
      if (!this.competencies.length) {
        this.competency_exercise = [];
        return;
      }
      // اگر تمرینی وجود ندارد
      if (!exercises.length) {
        this.competencies.map((competency) => {
          const exists = this.competency_exercise.find(
            (x) => x.id === competency.id
          );
          if (!exists) {
            competency.nickname = pathOr(null, ["caption"], competency);
            competency.exercises = pathOr([], ["exercises"], competency);
            // شایستگی را به دیتای نهایی اضافه میکنیم
            this.competency_exercise.push(competency);
          }
        });
        return;
      }
      // حلقه روی شایستگی‌های انتخاب شده
      this.competencies.map((competency) => {
        // حلقه روی تمرین‌هایی که از سرویس تمرین‌های مرتبط با شایستگی‌های انتخاب شده دریافت شده است
        exercises.map((exercise) => {
          const ex = { ...exercise };
          ex.nickname = pathOr(exercise.caption, ["nickname"], exercise);
          const exists = this.competency_exercise.find(
            (x) => x.id === competency.id
          );
          // اگر شایستگی در دیتای نهایی وجود داشت
          if (exists) {
            // اگر تمرین مودر نظر در دیتای تمرین‌های شایستگی نبود
            if (!exists.exercises.find((x) => x.id == exercise.id)) {
              // چک میکنیم تمرین شایستگی را احصا می کند
              ex.selected = !!exercise.competencies.find(
                (x) => x.id === competency.id
              );

              // در لیست تمرین‌های شایستگی اضافه میکنیم
              exists.exercises.push(ex);
            }
          }
          // اگر شایستگی در دیتا وجود نداشت
          else {
            // چک میکنیم که شایستگی توسط تمرین احصا می شود

            ex.selected = !!exercise.competencies.find(
              (x) => x.id === competency.id
            );

            // اگر nickname وجودد نداشت
            if (!("nickname" in competency)) {
              competency.nickname = competency.caption;
            }
            competency.exercises = pathOr([], ["exercises"], competency);

            // لیست تمرین‌های شایستگی را میسازیم
            // شایستگی را به دیتای نهایی اضافه میکنیم
            const comp = {
              id: competency.id,
              caption: pathOr(null, ["caption"], competency),
              nickname: pathOr(null, ["nickname"], competency),
              reference: pathOr(null, ["reference"], competency),
              exercises: [ex],
            };
            this.competency_exercise.push(comp);
          }
        });
      });
    },
    getExerciseInterSect(exercises) {
      if (this.exercises.length == 0) return exercises;
      let intersect = exercises.filter((x) => {
        return this.exercises.find((e) => e.id == x.id);
      });
      return intersect;
    },
    async getExercisesWithAllowedCompetencies() {
      try {
        if (!this.competencies.length) return [];
        const competenciesId = this.competencies
          .map((x) => "competency_id[]=" + x.id)
          .join("&");
        let url = generateApiUrl(["kanoon", "exercise_design"]);
        url = `${url}?${competenciesId}`;
        const response = await this.$httpService.get(url);
        return pathOr([], ["data", "data"], response);
      } catch (error) {
        this.$generateError(error);
        return [];
      }
    },
    async updateItems() {
      const exercises = await this.getExercisesWithAllowedCompetencies();
      const intersect_exercises = this.getExerciseInterSect(exercises);
      this.rebuildCompetencyExercise(intersect_exercises);
    },
    addCompetencies() {
      this.updateItems();
    },
    addExercise() {
      this.updateItems();
    },
    fillCompetencyExercise(items) {
      items.map((item) => {
        const coverage_percent = item.coverage_percent;
        const achieved_indicators = item.achieved_indicators;
        // اگر شایستگی آیتم در لیست شایستگی‌ها موجود نبود
        const alreadyCompetency = this.competencies.find(
          (x) => x.id === pathOr(null, ["competency", "id"], item)
        );
        if (!alreadyCompetency) {
          /* let myCompetency = {
            ...item.competency,
            ...item.indicator_count,
            ...item.coverage_percent,
            ...item.achieved_indicators,
          }; */
          this.competencies.push(item);
        }

        // اگر تمرین آیتم در لیست تمرین‌ها موجود نبود
        const alreadyExercise = this.exercises.find(
          (x) => x.id === pathOr(null, ["exercise", "id"], item)
        );
        if (!alreadyExercise) {
          this.exercises.push({
            ...item.exercise,
          });
        }

        // چک میکنیم آیتم در دیتای فعلی جدول وجود دارد
        const exists = this.competency_exercise.find(
          (x) => x.id == item.competency.id
        );
        const exercise = pathOr(null, ["exercise"], item);
        exercise.selected = pathOr(null, ["selected"], item);
        exercise.indicator_count = pathOr(null, ["indicator_count"], item);

        exercise.achieved_indicators = achieved_indicators;
        if (exists) {
          // آیتم در دیتا وجود داشت
          exists.exercises.push(exercise);
        } else {
          // آیتم در دیتا وجود نداشت
          const competency = item.competency;
          competency.coverage_percent = item.coverage_percent;
          competency.exercises = [exercise];
          this.competency_exercise.push(competency);
        }
      });
    },
    async getCompetencyExercises() {
      try {
        this.competencyExerciseLoading = true;
        const url = generateApiUrl(
          ["kanoon", "template_competency_exercise"],
          [this.id]
        );
        const response = await this.$httpService.get(url);
        this.competencies = pathOr([], ["data", "data"], response);
        this.exercises = pathOr([], ["0", "exercise"], this.competencies);
        // this.fillCompetencyExercise(data);
        await this.updateItems();
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.competencyExerciseLoading = false;
      }
    },
    async fetchData() {
      if (!this.id) return;
      try {
        this.isLoading = true;
        const url = generateApiUrl(["kanoon", "template_id"], [this.id]);
        const response = await this.$httpService.get(url);
        const data = pathOr(null, ["data", "data"], response);
        this.is_enable = pathOr(null, ["is_enable"], data);
        this.caption = pathOr(null, ["caption"], data);
        this.isEditable = pathOr(false, ["is_editable"], data);
        this.permission = response.data.permissions;
        this.competency_exercise = [];
        this.competencies = [];

        const currentCompanyCode = pathOr(null, ["company", "code"], data);
        const alreadyOption = this.company_options.some(
          ({ value }) => value === currentCompanyCode
        );
        if (!alreadyOption) {
          this.company_options.unshift({
            value: currentCompanyCode,
            text: pathOr(null, ["company", "caption"], data),
          });
        }
        this.company_id = this.company_options.find(
          ({ value }) => value === currentCompanyCode
        );

        this.category_selected = {
          text: pathOr(null, ["category", "caption"], data),
          value: pathOr(null, ["category", "id"], data),
        };
        this.level_selected = {
          text: pathOr(null, ["level", "caption"], data),
          value: pathOr(null, ["level", "id"], data),
        };
        this.indicatorCategoryRatioControl = {
          ...pathOr(null, ["indicator_category_ratio_control_id"], data),
          caption: pathOr(
            null,
            ["indicator_category_ratio_control_id", "name"],
            data
          ),
        };
        this.indicatorCategoryRatioControlOptions = [
          this.indicatorCategoryRatioControl,
        ];
        this.indicatorCategoryRatioControlValue = pathOr(
          null,
          ["indicator_category_ratio_control_value"],
          data
        );
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.isLoading = false;
      }
    },
    async getAllCompetencyExercise() {
      try {
        this.competencyExerciseLoading = true;
        const url = generateApiUrl(
          ["kanoon", "template_competency_exercise2"],
          [this.id]
        );
        const response = await this.$httpService.get(url);
        this.allCompetencies = pathOr([], ["data", "data"], response);
        this.allExercises = pathOr([], ["0", "exercise"], this.allCompetencies);
        this.renderExercisesAndCompetencies();
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.competencyExerciseLoading = false;
      }
    },
    levelSelectedUpdateCheckValidation(data) {
      this.levelSelectedCheckValidation = data;
    },
    getLevelSelected(data) {
      this.level_selected = data;
    },
    categorySelectedUpdateCheckValidation(data) {
      this.categorySelectedCheckValidation = data;
    },
    getCategorySelected(data) {
      this.category_selected = data;
    },
  },
};
</script>
